import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Box, Button, TextField, Typography, Paper, CircularProgress, Chip, Alert, MenuItem,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer, ToggleButton, ToggleButtonGroup, Pagination, Link,
} from '@mui/material';
import PlatformCustomerDetail from './PlatformCustomerDetail';
import {
  toYmd, daysAgo, firstOfMonth, num, baht, usdAsBaht, FX_LABEL, pct, date, dateTime, REVENUE_TYPES, MARKETPLACE_LABEL, COUPON_TYPE_LABEL,
} from './format';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const REPORT_URL = `${BACKEND_URL}/api/report/platform-customers`;

const PRESETS = [
  { id: '7d', label: '7 วัน', range: () => ({ from: daysAgo(6), to: toYmd(new Date()) }) },
  { id: '30d', label: '30 วัน', range: () => ({ from: daysAgo(29), to: toYmd(new Date()) }) },
  { id: 'month', label: 'เดือนนี้', range: () => ({ from: firstOfMonth(), to: toYmd(new Date()) }) },
];

const SORTS = [
  { id: 'revenue', label: 'ยอดขาย' },
  { id: 'lastActive', label: 'ใช้งานล่าสุด' },
  { id: 'translationCost', label: 'ค่าแปล' },
  { id: 'joined', label: 'วันที่สมัคร' },
];

function StatCard({ label, value, sub, dark }) {
  return (
    <Paper variant={dark ? 'elevation' : 'outlined'} sx={{
      p: 2, flex: '1 1 200px', minWidth: 0,
      ...(dark && { bgcolor: 'grey.900', color: 'common.white' }),
    }}>
      <Typography variant="body2" sx={{ opacity: 0.8 }}>{label}</Typography>
      <Typography variant="h5" fontWeight="bold" sx={{ fontVariantNumeric: 'tabular-nums' }}>{value}</Typography>
      {sub && <Typography variant="caption" sx={{ opacity: 0.8 }}>{sub}</Typography>}
    </Paper>
  );
}

function Section({ title, sub, children }) {
  return (
    <Box sx={{ mb: 4 }}>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1.5, mb: 1.5, flexWrap: 'wrap' }}>
        <Typography variant="h6" fontWeight="bold">{title}</Typography>
        {sub && <Typography variant="body2" color="text.secondary">{sub}</Typography>}
      </Box>
      {children}
    </Box>
  );
}

function Bar({ value, max, color = 'primary.main' }) {
  const width = max ? Math.min(100, (Math.abs(value) / max) * 100) : 0;
  return (
    <Box sx={{ height: 10, bgcolor: 'grey.200', borderRadius: 1, minWidth: 80 }}>
      <Box sx={{ height: 10, width: `${width}%`, bgcolor: color, borderRadius: 1 }} />
    </Box>
  );
}

/** Bars per day; each day's bar is split into the given series, bottom up. */
function DailyBars({ days, series, height = 120 }) {
  const max = Math.max(1, ...days.map((d) => series.reduce((s, x) => s + (d[x.key] || 0), 0)));
  return (
    <Box>
      <Box role="img" aria-label="กราฟรายวัน" sx={{ display: 'flex', alignItems: 'flex-end', gap: '3px', height, borderBottom: 1, borderColor: 'divider' }}>
        {days.map((d) => (
          <Box key={d.date} title={`${d.date}: ${series.map((x) => `${x.label} ${num(d[x.key])}`).join(' · ')}`}
            sx={{ flex: '1 1 0', display: 'flex', flexDirection: 'column-reverse', height: '100%' }}>
            {series.map((x) => (
              <Box key={x.key} sx={{ height: `${((d[x.key] || 0) / max) * 100}%`, bgcolor: x.color }} />
            ))}
          </Box>
        ))}
      </Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
        <Typography variant="caption" color="text.secondary">{days[0]?.date || ''}</Typography>
        <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
          {series.map((x) => (
            <Typography key={x.key} variant="caption" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Box component="span" sx={{ width: 8, height: 8, bgcolor: x.color, borderRadius: 0.5, display: 'inline-block' }} />{x.label}
            </Typography>
          ))}
        </Box>
        <Typography variant="caption" color="text.secondary">{days[days.length - 1]?.date || ''}</Typography>
      </Box>
    </Box>
  );
}

function TranslationCard({ t }) {
  const items = (m) => t.totals[m]?.items || 0;
  const originTotal = (t.byOrigin.customer || 0) + (t.byOrigin.system || 0);
  return (
    <Paper variant="outlined" sx={{ p: 2, flex: '1 1 520px', minWidth: 0 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 2 }}>
        <Typography fontWeight="bold">ระบบแปลภาษา</Typography>
        <Typography variant="caption" color="text.secondary">ทั้งเว็บ (รวมผู้ไม่ได้ล็อกอินและบอท) · เก็บ 90 วัน · {FX_LABEL}</Typography>
      </Box>
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 2, mb: 2 }}>
        <StatMini label="ฟรี" value={num(items('free'))} sub={`แปลฟรี ${t.freeRate == null ? '—' : `${(t.freeRate * 100).toFixed(1)}%`}`} />
        <StatMini label="จากคลัง" value={num(items('cache'))} sub="ไม่ต้องแปล" />
        <StatMini label="เสียเงิน" value={num(items('paid'))} sub={`ค่าแปล ${usdAsBaht(t.costUsd)}`} />
        <StatMini label="ถูกบล็อก" value={num(items('blocked'))} sub="ปิดแปลเสียเงิน" />
      </Box>
      <DailyBars days={t.daily} series={[
        { key: 'free', label: 'ฟรี', color: '#2F62D9' },
        { key: 'cache', label: 'คลัง', color: '#A9BEEB' },
        { key: 'paid', label: 'เสียเงิน', color: '#E07B1F' },
        { key: 'blocked', label: 'บล็อก', color: '#3A3F48' },
      ]} />
      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 2, mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
        <StatMini label="ลูกค้า / ระบบ" value={`${pct(t.byOrigin.customer || 0, originTotal)} / ${pct(t.byOrigin.system || 0, originTotal)}`} />
        <StatMini label="จากบอท" value={`${num(t.bots.events)} ครั้ง`} sub={`เสียเงิน ${num(t.bots.paid)} รายการ`} />
        <StatMini label="สาเหตุแปลฟรีไม่สำเร็จอันดับ 1" value={t.topReason ? t.topReason.reason : '—'} sub={t.topReason ? `${num(t.topReason.count)} ครั้ง` : ''} />
      </Box>
    </Paper>
  );
}

function StatMini({ label, value, sub }) {
  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <Typography fontWeight="bold" sx={{ fontVariantNumeric: 'tabular-nums', wordBreak: 'break-word' }}>{value}</Typography>
      {sub && <Typography variant="caption" color="text.secondary">{sub}</Typography>}
    </Box>
  );
}

const OUTBOUND = {
  paypay: { label: 'PayPay', blockedLabel: 'ถูกบล็อก (429 / 403)' },
  mercari: { label: 'Mercari', blockedLabel: 'ถูกจำกัด (429)' },
};
const SOURCE_LABEL = { web: 'หน้าเว็บ', poller: 'poller', cron: 'cron', background: 'งานเบื้องหลัง' };

function OutboundCard({ outbound }) {
  const [tab, setTab] = useState('paypay');
  const o = outbound[tab];
  const meta = OUTBOUND[tab];
  return (
    <Paper variant="outlined" sx={{ p: 2, flex: '1 1 520px', minWidth: 0 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <Typography fontWeight="bold">คำขอออกไปยังร้านค้า</Typography>
          <ToggleButtonGroup size="small" exclusive value={tab} onChange={(_, v) => v && setTab(v)}>
            <ToggleButton value="paypay">PayPay</ToggleButton>
            <ToggleButton value="mercari">Mercari</ToggleButton>
          </ToggleButtonGroup>
        </Box>
        <Typography variant="caption" color="text.secondary">{tab === 'paypay' ? 'paypay_requests' : 'mercari_requests'} · เก็บ {o.retentionDays} วัน</Typography>
      </Box>

      <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
        {tab === 'paypay' && (
          <>
            <Chip size="small" color={o.killSwitch ? 'error' : 'success'} label={`Kill switch: ${o.killSwitch ? 'เปิด (หยุดเรียก PayPay)' : 'ปิด'}`} />
            <Chip size="small" color={o.cooldownActive ? 'warning' : 'success'} label={o.cooldownActive ? 'กำลังพักหลังถูกบล็อก' : 'ไม่ได้ถูกบล็อก'} />
          </>
        )}
        {tab === 'mercari' && (
          <Chip size="small" variant="outlined"
            label={`Poller: ${num(o.pollers.runs)} รอบ · ตรวจ ${num(o.pollers.checked)} · 429 ${num(o.pollers.rateLimited)}`} />
        )}
        <Chip size="small" variant="outlined"
          label={o.lastBlock ? `ถูกบล็อกล่าสุด ${dateTime(o.lastBlock.at)}${o.lastBlock.retryAfter ? ` · retry-after ${o.lastBlock.retryAfter}` : ''}` : 'ยังไม่เคยถูกบล็อก'} />
      </Box>

      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 2, mb: 2 }}>
        <StatMini label="คำขอทั้งหมด" value={num(o.requests)} sub={`เฉลี่ย ${num(o.avgMs)} ms`} />
        <StatMini label="สำเร็จ (2xx)" value={pct(o.ok, o.requests)} sub={num(o.ok)} />
        <StatMini label={meta.blockedLabel} value={num(o.blocked)} />
        <StatMini label="ไม่ตอบ / 5xx" value={`${num(o.timeouts)} / ${num(o.serverErrors)}`} />
      </Box>

      <DailyBars height={80} days={o.daily.map((d) => ({ ...d, okish: d.requests - d.blocked }))} series={[
        { key: 'okish', label: 'คำขอ', color: '#A9BEEB' },
        { key: 'blocked', label: 'ถูกบล็อก', color: '#E07B1F' },
      ]} />

      <TableContainer sx={{ mt: 2 }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Endpoint</TableCell>
              <TableCell align="right">คำขอ</TableCell>
              <TableCell align="right">ถูกบล็อก</TableCell>
              <TableCell align="right">เฉลี่ย ms</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {o.byEndpoint.length === 0 && (
              <TableRow><TableCell colSpan={4} align="center">ไม่มีข้อมูลในช่วงนี้</TableCell></TableRow>
            )}
            {o.byEndpoint.map((e) => (
              <TableRow key={e.endpoint}>
                <TableCell>{e.endpoint}</TableCell>
                <TableCell align="right">{num(e.requests)}</TableCell>
                <TableCell align="right">{num(e.blocked)}</TableCell>
                <TableCell align="right">{num(e.avgMs)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <Typography variant="caption" color="text.secondary" display="block" mt={1}>
        ส่งจาก: {o.bySource.length ? o.bySource.map((s) => `${SOURCE_LABEL[s.source] || s.source} ${pct(s.requests, o.requests)}`).join(' · ') : '—'}
        {tab === 'paypay' ? ' · PayPay ไม่เก็บว่าลูกค้าคนไหน' : ' · ดูรายลูกค้าได้ในหน้ารายละเอียด'}
      </Typography>
    </Paper>
  );
}

const SHIPPING_MODES = [
  { id: 'all', label: 'ทั้งหมด' },
  { id: 'air', label: 'แอร์' },
  { id: 'sea', label: 'เรือ' },
  { id: 'unassigned', label: 'ยังไม่มีรอบส่ง' },
];

function RevenueSection({ revenue: allRevenue, coupons }) {
  const [mode, setMode] = useState('all');
  const revenue = mode === 'all' ? allRevenue : allRevenue.byShipping[mode];
  const maxType = Math.max(1, ...REVENUE_TYPES.map((t) => revenue.byType[t.key].amount));
  const maxMarket = Math.max(1, ...revenue.marketplaces.map((m) => m.total));
  return (
    <>
      <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', flexWrap: 'wrap', mb: 2 }}>
        <ToggleButtonGroup size="small" exclusive value={mode} onChange={(_, v) => v && setMode(v)}>
          {SHIPPING_MODES.map((m) => (
            <ToggleButton key={m.id} value={m.id}>
              {m.label} ({num(m.id === 'all' ? allRevenue.orders : allRevenue.byShipping[m.id].orders)})
            </ToggleButton>
          ))}
        </ToggleButtonGroup>
        <Typography variant="caption" color="text.secondary">
          แอร์ / เรือ ตามรอบส่งของออเดอร์ · ยังไม่มีรอบส่ง = ของยังอยู่ญี่ปุ่น
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 2 }}>
        <StatCard dark label="ยอดขายรวม" value={baht(revenue.total)} sub="ก่อนหักคูปอง" />
        <StatCard label="ส่วนลดคูปอง" value={`−${baht(revenue.coupons)}`}
          sub={mode === 'all' ? `${num(coupons.ordersWithCoupon)} ออเดอร์ใช้คูปอง` : 'คูปองของออเดอร์กลุ่มนี้'} />
        <StatCard dark label="ยอดขายหลังหักคูปอง" value={baht(revenue.afterCoupons)} sub="ที่ลูกค้าจ่ายจริง" />
        <StatCard label="ออเดอร์" value={num(revenue.orders)} sub="ไม่รวมที่ร้านยกเลิก" />
        <StatCard label="เฉลี่ยต่อออเดอร์" value={baht(revenue.orders ? revenue.afterCoupons / revenue.orders : 0)} sub="หลังหักคูปอง" />
      </Box>

      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
        <Paper variant="outlined" sx={{ p: 2, flex: '999 1 600px', minWidth: 0 }}>
          <Typography fontWeight="bold" mb={1}>แยกตามประเภท</Typography>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>ประเภท</TableCell>
                  <TableCell sx={{ width: '30%' }}>สัดส่วน</TableCell>
                  <TableCell align="right">ออเดอร์</TableCell>
                  <TableCell align="right">ยอด</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {REVENUE_TYPES.map((t) => (
                  <TableRow key={t.key}>
                    <TableCell>
                      {t.label}
                      <Typography variant="caption" display="block" color="text.secondary" sx={{ fontFamily: 'monospace' }}>{t.field}</Typography>
                    </TableCell>
                    <TableCell><Bar value={revenue.byType[t.key].amount} max={maxType} /></TableCell>
                    <TableCell align="right">{num(revenue.byType[t.key].orders)}</TableCell>
                    <TableCell align="right">{baht(revenue.byType[t.key].amount)}</TableCell>
                  </TableRow>
                ))}
                <TableRow sx={{ '& td': { fontWeight: 'bold', borderTop: 2 } }}>
                  <TableCell>ยอดขายรวม</TableCell><TableCell /><TableCell align="right">{num(revenue.orders)}</TableCell>
                  <TableCell align="right">{baht(revenue.total)}</TableCell>
                </TableRow>
                <TableRow>
                  <TableCell>ส่วนลดคูปอง<Typography variant="caption" display="block" color="text.secondary" sx={{ fontFamily: 'monospace' }}>coupon_usages.discountAmount</Typography></TableCell>
                  <TableCell /><TableCell align="right">{mode === 'all' ? num(coupons.ordersWithCoupon) : ''}</TableCell>
                  <TableCell align="right" sx={{ color: 'warning.dark' }}>−{baht(revenue.coupons)}</TableCell>
                </TableRow>
                <TableRow sx={{ '& td': { fontWeight: 'bold' } }}>
                  <TableCell>ยอดขายหลังหักคูปอง</TableCell><TableCell /><TableCell />
                  <TableCell align="right">{baht(revenue.afterCoupons)}</TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>

        <Box sx={{ flex: '1 1 340px', minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Paper variant="outlined" sx={{ p: 2 }}>
            <Typography fontWeight="bold" mb={1.5}>แยกตามร้านค้า</Typography>
            {revenue.marketplaces.length === 0 && <Typography variant="body2" color="text.secondary">ไม่มีออเดอร์ในช่วงนี้</Typography>}
            {revenue.marketplaces.map((m) => (
              <Box key={m.marketplace} sx={{ mb: 1.5 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 1 }}>
                  <Typography variant="body2">{MARKETPLACE_LABEL[m.marketplace] || m.marketplace}</Typography>
                  <Typography variant="body2" sx={{ fontVariantNumeric: 'tabular-nums' }}>{baht(m.total)}</Typography>
                </Box>
                <Bar value={m.total} max={maxMarket} />
                <Typography variant="caption" color="text.secondary">หลังหักคูปอง {baht(m.afterCoupons)} · {num(m.orders)} ออเดอร์</Typography>
              </Box>
            ))}
          </Paper>
        </Box>
      </Box>

      <Paper variant="outlined" sx={{ p: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 1, mb: 2 }}>
          <Typography fontWeight="bold">การใช้คูปอง</Typography>
          <Typography variant="caption" color="text.secondary">ใช้ = วันที่ใช้คูปองอยู่ในช่วงนี้ · แจก = วันที่ได้รับคูปองอยู่ในช่วงนี้</Typography>
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 2, mb: 2 }}>
          <StatMini label="ใช้คูปอง" value={`${num(coupons.used)} ครั้ง`} sub={`${pct(coupons.ordersWithCoupon, revenue.orders)} ของออเดอร์`} />
          <StatMini label="ส่วนลดที่ให้ไป" value={baht(coupons.discount)} sub={`${pct(coupons.discount, revenue.total)} ของยอดขายรวม`} />
          <StatMini label="ลูกค้าที่ใช้คูปอง" value={num(coupons.customers)} />
          <StatMini label="ส่วนลดเฉลี่ย" value={baht(coupons.avgDiscount)} sub="ต่อครั้ง" />
        </Box>
        <TableContainer>
          <Table size="small" sx={{ minWidth: 860 }}>
            <TableHead>
              <TableRow>
                <TableCell>คูปอง</TableCell>
                <TableCell>ประเภท</TableCell>
                <TableCell align="right">แจก</TableCell>
                <TableCell align="right">ใช้</TableCell>
                <TableCell align="right">ลูกค้า</TableCell>
                <TableCell align="right">ส่วนลด</TableCell>
                <TableCell align="right">ยอดออเดอร์ที่ใช้</TableCell>
                <TableCell align="right">แต้มที่แลก</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {coupons.coupons.length === 0 && (
                <TableRow><TableCell colSpan={8} align="center">ไม่มีการใช้หรือแจกคูปองในช่วงนี้</TableCell></TableRow>
              )}
              {coupons.coupons.map((c) => (
                <TableRow key={c.id}>
                  <TableCell>
                    <Typography variant="body2" fontWeight="bold">{c.name}</Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }}>{c.code}</Typography>
                  </TableCell>
                  <TableCell>
                    {COUPON_TYPE_LABEL[c.type] || c.type} {c.type === 'discount_percentage' ? `${num(c.value)}%` : baht(c.value)}
                    <Typography variant="caption" display="block" color="text.secondary">
                      {[c.minimumSpend ? `ขั้นต่ำ ${baht(c.minimumSpend)}` : null, c.isExchangeable === false ? 'แจกเท่านั้น' : null].filter(Boolean).join(' · ') || '—'}
                    </Typography>
                  </TableCell>
                  <TableCell align="right">{num(c.given)}</TableCell>
                  <TableCell align="right">{num(c.used)}</TableCell>
                  <TableCell align="right">{num(c.customers)}</TableCell>
                  <TableCell align="right" sx={{ color: 'warning.dark' }}>−{baht(c.discount)}</TableCell>
                  <TableCell align="right">{baht(c.orderAmount)}</TableCell>
                  <TableCell align="right">{num(c.pointsSpent)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </>
  );
}

function WalletTable({ title, sub, rows, onOpen, empty, negative }) {
  return (
    <Paper variant="outlined" sx={{ p: 2, flex: '1 1 480px', minWidth: 0 }}>
      <Typography fontWeight="bold">{title}</Typography>
      <Typography variant="caption" color="text.secondary" display="block" mb={1}>{sub}</Typography>
      <TableContainer sx={{ maxHeight: 520 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>#</TableCell>
              <TableCell>ลูกค้า</TableCell>
              <TableCell>Tier</TableCell>
              <TableCell>เคลื่อนไหวล่าสุด</TableCell>
              <TableCell align="right">ยอดเงิน</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.length === 0 && (
              <TableRow><TableCell colSpan={5} align="center">{empty}</TableCell></TableRow>
            )}
            {rows.map((w, i) => (
              <TableRow key={w.userId} hover>
                <TableCell>{i + 1}</TableCell>
                <TableCell>
                  <Link component="button" type="button" onClick={() => onOpen(w.userId)} fontWeight="bold" underline="hover">{w.name}</Link>
                  <Typography variant="caption" display="block" color="text.secondary" sx={{ fontFamily: 'monospace' }}>{w.tamaCode || `#${w.userId}`}</Typography>
                </TableCell>
                <TableCell>{w.tier || '—'}</TableCell>
                <TableCell>{dateTime(w.lastTransactionAt)}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 'bold', color: negative ? 'error.main' : undefined }}>{baht(w.balance)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Paper>
  );
}

function WalletSection({ wallet, onOpen }) {
  return (
    <>
      <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 2 }}>
        <StatCard label="เงินคงเหลือใน wallet (ยอดบวก)" value={baht(wallet.balance)} sub={`${num(wallet.customersWithBalance)} ลูกค้า`} />
        <StatCard label="wallet ติดลบ (ลูกค้าค้างจ่าย)" value={baht(wallet.negativeBalance)} sub={`${num(wallet.customersNegative)} ลูกค้า`} />
        <StatCard label="เติมเงินเข้า wallet (ช่วงนี้)" value={baht(wallet.deposits)} />
        <StatCard label="คืนเงินเข้า wallet (ช่วงนี้)" value={baht(wallet.refunds)} />
      </Box>
      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <WalletTable title="ยอดเงินคงเหลือสูงสุด 20 อันดับ" sub="เฉพาะ wallet ที่มากกว่า ฿0 · ยอดตอนนี้ ไม่ขึ้นกับช่วงวันที่"
          rows={wallet.topBalances} onOpen={onOpen} empty="ไม่มีลูกค้าที่มีเงินใน wallet" />
        <WalletTable negative title="wallet ติดลบ" sub="ทุกคนที่ยอดต่ำกว่า ฿0 · ติดลบมากสุดอยู่บน · ยอดตอนนี้"
          rows={wallet.negativeBalances} onOpen={onOpen} empty="ไม่มี wallet ติดลบ" />
      </Box>
    </>
  );
}

function CustomerList({ range, onOpen }) {
  const [q, setQ] = useState('');
  const [query, setQuery] = useState('');
  const [sort, setSort] = useState('revenue');
  const [page, setPage] = useState(1);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    axios.get(`${REPORT_URL}/customers`, { params: { ...range, q: query || undefined, sort, page, pageSize: 50 } })
      .then((res) => { if (!cancelled) setData(res.data); })
      .catch((err) => { if (!cancelled) setError(err.response?.data?.message || 'โหลดรายชื่อลูกค้าไม่สำเร็จ'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [range, query, sort, page]);

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Box component="form" onSubmit={(e) => { e.preventDefault(); setPage(1); setQuery(q.trim()); }}
        sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'center', mb: 2 }}>
        <Typography fontWeight="bold" sx={{ mr: 'auto' }}>
          รายชื่อลูกค้า {data ? `(${num(data.total)})` : ''}
        </Typography>
        <TextField size="small" label="ค้นหา ชื่อ / tamaCode" value={q} onChange={(e) => setQ(e.target.value)} sx={{ minWidth: 240 }} />
        <Button type="submit" variant="outlined">ค้นหา</Button>
        <TextField select size="small" label="เรียงตาม" value={sort} onChange={(e) => { setPage(1); setSort(e.target.value); }} sx={{ minWidth: 160 }}>
          {SORTS.map((s) => <MenuItem key={s.id} value={s.id}>{s.label}</MenuItem>)}
        </TextField>
        {loading && <CircularProgress size={20} />}
      </Box>
      <Typography variant="caption" color="text.secondary" display="block" mb={1}>
        {query ? `ผลค้นหา "${query}" จากลูกค้าทั้งหมด` : 'ลูกค้าที่มีการใช้งานในช่วงวันที่ที่เลือก'} · ตัวเลขในตารางนับเฉพาะช่วงวันที่ที่เลือก · ค่าแปล{FX_LABEL}
      </Typography>
      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      <TableContainer>
        <Table size="small" sx={{ minWidth: 1100 }}>
          <TableHead>
            <TableRow>
              <TableCell>ลูกค้า</TableCell>
              <TableCell>Tier</TableCell>
              <TableCell>สมัคร</TableCell>
              <TableCell>ใช้งานล่าสุด</TableCell>
              <TableCell align="right">ค้นหา</TableCell>
              <TableCell align="right">ดูสินค้า</TableCell>
              <TableCell align="right">ค่าแปล</TableCell>
              <TableCell align="right">ต่อราคา</TableCell>
              <TableCell align="right">ออเดอร์</TableCell>
              <TableCell align="right">คูปอง</TableCell>
              <TableCell align="right">ยอดขาย</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data?.rows.length === 0 && (
              <TableRow><TableCell colSpan={11} align="center">ไม่มีลูกค้า</TableCell></TableRow>
            )}
            {data?.rows.map((c) => (
              <TableRow key={c.userId} hover>
                <TableCell>
                  <Link component="button" type="button" onClick={() => onOpen(c.userId)} fontWeight="bold" underline="hover">{c.name}</Link>
                  {c.isTestAccount && <Chip size="small" color="error" label="ทดสอบ" sx={{ ml: 1 }} />}
                  <Typography variant="caption" display="block" color="text.secondary" sx={{ fontFamily: 'monospace' }}>{c.tamaCode || `#${c.userId}`}</Typography>
                </TableCell>
                <TableCell>{c.tier || '—'}</TableCell>
                <TableCell>{date(c.joinedAt)}</TableCell>
                <TableCell>{dateTime(c.lastActiveAt)}</TableCell>
                <TableCell align="right">{num(c.searches)}</TableCell>
                <TableCell align="right">{num(c.itemViews)}</TableCell>
                <TableCell align="right">{usdAsBaht(c.translationCostUsd)}</TableCell>
                <TableCell align="right">{num(c.negotiations)}</TableCell>
                <TableCell align="right">{num(c.orders)}</TableCell>
                <TableCell align="right" sx={{ color: c.coupons ? 'warning.dark' : undefined }}>{c.coupons ? `−${baht(c.coupons)}` : '—'}</TableCell>
                <TableCell align="right">
                  <Typography variant="body2" fontWeight="bold">{baht(c.revenue)}</Typography>
                  <Typography variant="caption" color="text.secondary">หลังคูปอง {baht(c.afterCoupons)}</Typography>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      {data && data.total > data.pageSize && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Pagination page={data.page} count={Math.ceil(data.total / data.pageSize)} onChange={(_, p) => setPage(p)} />
        </Box>
      )}
    </Paper>
  );
}

/**
 * Platform customers (accounts on the Tamachan site): usage and what it costs us,
 * who they are, and the money they bring in. Click a customer for their detail.
 */
const PlatformCustomerReport = () => {
  const [preset, setPreset] = useState('30d');
  const [range, setRange] = useState(PRESETS[1].range());
  const [draft, setDraft] = useState(range);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [openUserId, setOpenUserId] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError('');
    axios.get(REPORT_URL, { params: range })
      .then((res) => { if (!cancelled) setData(res.data); })
      .catch((err) => { if (!cancelled) setError(err.response?.data?.message || 'โหลดรายงานไม่สำเร็จ'); })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [range]);

  const pickPreset = (id) => {
    if (!id) return;
    setPreset(id);
    if (id === 'custom') return;
    const next = PRESETS.find((p) => p.id === id).range();
    setDraft(next);
    setRange(next);
  };

  if (openUserId) {
    return <PlatformCustomerDetail userId={openUserId} range={range} onBack={() => setOpenUserId(null)} />;
  }

  const users = data?.users;
  const revenue = data?.revenue;

  return (
    <Box sx={{ width: '100%', maxWidth: 1360, p: { xs: 1, sm: 2 } }}>
      <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center', mb: 3, flexWrap: 'wrap' }}>
        <ToggleButtonGroup size="small" exclusive value={preset} onChange={(_, v) => pickPreset(v)}>
          {PRESETS.map((p) => <ToggleButton key={p.id} value={p.id}>{p.label}</ToggleButton>)}
          <ToggleButton value="custom">กำหนดเอง</ToggleButton>
        </ToggleButtonGroup>
        {preset === 'custom' && (
          <>
            <TextField label="ตั้งแต่" type="date" size="small" value={draft.from}
              onChange={(e) => setDraft({ ...draft, from: e.target.value })} InputLabelProps={{ shrink: true }} />
            <TextField label="ถึง" type="date" size="small" value={draft.to}
              onChange={(e) => setDraft({ ...draft, to: e.target.value })} InputLabelProps={{ shrink: true }} />
            <Button variant="contained" onClick={() => setRange({ ...draft })}>ดูรายงาน</Button>
          </>
        )}
        <Typography variant="body2" color="text.secondary">{range.from} → {range.to}</Typography>
        {loading && <CircularProgress size={20} />}
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      {data && (
        <>
          <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', mb: 4 }}>
            <StatCard label="ลูกค้าบนแพลตฟอร์ม" value={num(users.total)}
              sub={`สมัครใหม่ ${num(users.newUsers)} คนในช่วงนี้${users.testAccounts ? ` · ไม่รวมบัญชีทดสอบ ${num(users.testAccounts)}` : ''}`} />
            <StatCard label="ลูกค้าที่ใช้งาน" value={num(users.active)} sub={`${pct(users.active, users.total)} ของทั้งหมด`} />
            <StatCard dark label="ยอดขายรวม" value={baht(revenue.total)} sub={`หลังหักคูปอง ${baht(revenue.afterCoupons)}`} />
            <StatCard label="ค่าแปลภาษา (เสียเงิน)" value={usdAsBaht(data.translation.costUsd)} sub={`Google Translate · ${FX_LABEL}`} />
            <StatCard label="ยอดขายต่อลูกค้าที่ใช้งาน" value={baht(users.active ? revenue.afterCoupons / users.active : 0)} sub="หลังหักคูปอง ÷ ลูกค้าที่ใช้งาน" />
          </Box>

          <Section title="1 · การใช้งานและค่าใช้จ่าย" sub="สิ่งที่ลูกค้าทำให้เราต้องจ่าย">
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              <TranslationCard t={data.translation} />
              <OutboundCard outbound={data.outbound} />
            </Box>
          </Section>

          <Section title="2 · ลูกค้า" sub="ใครใช้งาน และไปถึงขั้นไหน">
            <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
              <Typography fontWeight="bold" mb={1.5}>ลูกค้าที่สมัครในช่วงนี้ ไปถึงขั้นไหนแล้ว</Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                {[
                  ['สมัคร', data.funnel.signedUp],
                  ['ค้นหาสินค้า (Mercari)', data.funnel.searched],
                  ['ดูหน้าสินค้า (Mercari)', data.funnel.viewedItem],
                  ['ส่งคำขอซื้อ', data.funnel.purchaseRequest],
                  ['มีออเดอร์', data.funnel.hasOrder],
                ].map(([label, n]) => (
                  <Box key={label} sx={{ flex: '1 1 160px' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2">{label}</Typography>
                      <Typography variant="body2" color="text.secondary">{pct(n, data.funnel.signedUp)}</Typography>
                    </Box>
                    <Bar value={n} max={data.funnel.signedUp} />
                    <Typography variant="h6" sx={{ fontVariantNumeric: 'tabular-nums' }}>{num(n)}</Typography>
                  </Box>
                ))}
              </Box>
            </Paper>
            <CustomerList range={range} onOpen={setOpenUserId} />
          </Section>

          <Section title="3 · ยอดขาย" sub="ออเดอร์ของลูกค้าแพลตฟอร์ม นับตามวันที่สั่ง · ก่อนและหลังหักคูปอง">
            <RevenueSection revenue={revenue} coupons={data.coupons} />
          </Section>

          <Section title="4 · Wallet" sub="เงินที่ลูกค้าฝากไว้กับเรา และที่ลูกค้าค้างจ่าย">
            <WalletSection wallet={data.wallet} onOpen={setOpenUserId} />
          </Section>
        </>
      )}
    </Box>
  );
};

export default PlatformCustomerReport;
